import CodeBlock from '../CodeBlock';

function FnSignature({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-left font-mono text-sm bg-gray-900/50 border border-gray-800 rounded-lg px-4 py-2.5 mb-3 text-gray-300 overflow-x-auto">
      {children}
    </div>
  );
}

const ENDPOINTS: Array<[string, string, string, string]> = [
  ['POST', '/api/v1/sweeps/launch', 'sweeps:write', '202 · 402 · 422'],
  ['GET', '/api/v1/sweeps', 'sweeps:read', '200'],
  ['GET', '/api/v1/sweeps/{sweep_id}', 'sweeps:read', '200 · 404'],
  ['POST', '/api/v1/sweeps/{sweep_id}/cancel', 'sweeps:write', '200 · 404 · 409'],
];

const JOB_FIELDS: Array<[string, string, string]> = [
  ['sweep_id', 'str', 'swp_ + a 26-character ULID — not a UUID'],
  ['status', 'str', 'queued → launching → running → completed | failed | cancelled'],
  ['estimate', 'object', 'The sizing/cost estimate computed at launch'],
  ['plan', 'object', 'The validated plan, as stored'],
  ['error', 'str | null', 'Failure reason once status is failed'],
  ['leaderboard', 'array', 'Ranked param sets — present only once completed'],
  ['per_pair', 'array', 'Pairs sweeps only; absent (not null) on symbol sweeps'],
  ['estimate_deviation', 'object', 'actual − estimated, for windows and total_backtests'],
  ['usage_receipt', 'object', 'gpu, gpu_seconds, compute cost, platform fee, total'],
];

export default function DocsSweepsModule() {
  return (
    <section data-docs-section="sweeps" className="text-left mb-16">
      <h2 className="text-2xl font-bold text-white mb-2 group">
        <a href="#sweeps" className="hover:text-sky-400 transition-colors">GPU Parameter Sweeps</a>
        <span className="ml-2 opacity-0 group-hover:opacity-100 text-gray-600 transition-opacity">#</span>
      </h2>
      <p className="text-gray-400 mb-3">
        A sweep is a plan in and a job id out. You describe the strategy, universe,
        parameter grid and walk-forward scheme as data; the platform expands the grid,
        runs every combination on GPU, and hands back a ranked leaderboard with an
        itemized usage receipt.
      </p>
      <div className="p-3 bg-gray-900/50 border border-gray-800 rounded-lg mb-6 text-sm text-gray-400">
        <strong className="text-gray-300">Auth:</strong> a scoped API key on the standard
        bearer header — <code className="text-sky-400">Authorization: Bearer pa_live_…</code>.
        There is no <code className="text-gray-300">X-API-Key</code>. A session JWT works on
        the same header and carries no scope restrictions.
      </div>

      {/* Auth */}
      <div id="sweeps-auth" data-docs-section="sweeps-auth" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">Getting a key</h3>
        <p className="text-gray-400 text-sm mb-3">
          Key management is JWT-only — an API key can never mint or revoke another key.
          The <code className="text-gray-300">secret</code> is returned exactly once.
        </p>
        <CodeBlock
          language="bash"
          code={`$ export PROPALGOS_TOKEN=<your session jwt>
$ propalgos keys create --name agent-worker \\
    --scope sweeps:read --scope sweeps:write \\
    --scope feeds:read --scope feeds:write --json

$ export PROPALGOS_API_KEY=pa_live_REPLACEWITHTHESECRETSHOWNABOVE
# or persist it in the OS keyring (needs the [keyring] extra)
$ propalgos keys use pa_live_REPLACEWITHTHESECRETSHOWNABOVE`}
        />
        <p className="text-gray-400 text-sm">
          Four scopes exist: <code className="text-sky-400">sweeps:read</code>,{' '}
          <code className="text-sky-400">sweeps:write</code>,{' '}
          <code className="text-sky-400">feeds:read</code>,{' '}
          <code className="text-sky-400">feeds:write</code>. A key missing one fails at the
          request with <code className="text-gray-300">403</code>, not at creation. Keys are
          rate-limited per key per 60s window (<code className="text-gray-300">rate_limit_per_min</code>,
          default 60) — over the limit is <code className="text-gray-300">429</code>.
        </p>
      </div>

      {/* Endpoints */}
      <div id="sweeps-endpoints" data-docs-section="sweeps-endpoints" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-3">Endpoints</h3>
        <div className="overflow-x-auto">
          <table className="text-sm text-left w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="py-2 pr-4 text-gray-400 font-medium">Method</th>
                <th className="py-2 pr-4 text-gray-400 font-medium">Path</th>
                <th className="py-2 pr-4 text-gray-400 font-medium">Scope</th>
                <th className="py-2 text-gray-400 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              {ENDPOINTS.map(([method, path, scope, status]) => (
                <tr key={`${method} ${path}`} className="border-b border-gray-800/50 last:border-0">
                  <td className="py-1.5 pr-4 font-mono text-amber-400">{method}</td>
                  <td className="py-1.5 pr-4 font-mono text-sky-400">{path}</td>
                  <td className="py-1.5 pr-4 text-gray-500 font-mono">{scope}</td>
                  <td className="py-1.5 text-gray-400 font-mono">{status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-gray-400 text-sm mt-3">
          Route-specific codes only. <code className="text-gray-300">401</code> (no or invalid
          credential), <code className="text-gray-300">403</code> (missing scope, or a
          suspended account) and <code className="text-gray-300">429</code> (per-key rate
          limit) come from the shared auth dependency and apply to <em>every</em> row on this
          surface and on Signal Feeds — listing them per row would suggest a row without them
          is unauthenticated.
        </p>
        <p className="text-gray-400 text-sm mt-3">
          There is deliberately no server-side estimate endpoint. Estimation is a pure
          function of the plan, so it runs offline with no credential and no network call.
          The server computes the same number during launch and echoes it back.
        </p>
        <p className="text-gray-400 text-sm mt-2">
          Ownership is enforced on every read: someone else&apos;s sweep is a{' '}
          <code className="text-gray-300">404</code>, never a{' '}
          <code className="text-gray-300">403</code>.
        </p>
      </div>

      {/* Plan */}
      <div id="sweeps-plan" data-docs-section="sweeps-plan" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">The plan</h3>
        <FnSignature>
          SweepPlan.from_dict(plan) → SweepPlan · plan.estimate(n_symbols, n_bars) → dict
        </FnSignature>
        <p className="text-gray-400 text-sm mb-3">
          Validation collects <em>every</em> problem before raising, so a bad plan is one
          round trip, not a game of whack-a-mole. Universe is one of three shapes:{' '}
          <code className="text-gray-300">{'{"select": "top_by_volume", "n": int}'}</code>,{' '}
          <code className="text-gray-300">{'{"symbols": [...]}'}</code>, or{' '}
          <code className="text-gray-300">{'{"select": "pairs", "screen": {...}}'}</code> — the
          last runs one synthetic spread column per surviving pair.
        </p>
        <CodeBlock
          language="json"
          filename="plan.json"
          code={`{
  "strategy": "bollinger_bands",
  "dataset": "crypto-kraken",
  "interval": "1h",
  "universe": { "select": "top_by_volume", "n": 200 },
  "lookback": "540d",
  "param_grid": { "period": [10, 20, 30], "num_std": [1.5, 2.0, 2.5, 3.0] },
  "walk_forward": {
    "scheme": "rolling", "train": "180d", "test": "30d",
    "step": "30d", "purge_gap": "24h"
  },
  "costs": { "fee_bps": 5.0, "slippage_bps": 3.0 },
  "rank_by": "oos_sharpe",
  "report": ["sharpe", "sortino", "max_drawdown", "trades", "oos_is_decay"]
}`}
        />
      </div>

      {/* CLI flow */}
      <div id="sweeps-cli" data-docs-section="sweeps-cli" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">Estimate, launch, wait</h3>
        <CodeBlock
          language="bash"
          code={`# offline — no credential, no network call
$ propalgos sweeps estimate --plan plan.json --json
{"combos": 36, "symbols": 200, "windows": 11, "total_backtests": 79200,
 "est_gpu_seconds": 16.63, "est_cost_usd": 0.0038, "calibration": {...}}

# launch and poll until terminal
$ propalgos sweeps launch --plan plan.json --wait --timeout 5400 --poll 15 --json

$ propalgos sweeps list --limit 20 --json
$ propalgos sweeps get swp_01M0VFATZ2SAJVE88JK2JVBCWX --json
$ propalgos sweeps cancel swp_01M0VFATZ2SAJVE88JK2JVBCWX --json`}
        />
        <p className="text-gray-400 text-sm mb-3">
          Pass <code className="text-sky-400">--idempotency-key &lt;uuid&gt;</code> if the caller
          might retry: a repeat with the same key returns the existing job instead of
          launching a second one. Launch is gated on prepaid balance covering{' '}
          <code className="text-gray-300">est_cost_usd + platform fee</code> — short is a{' '}
          <code className="text-gray-300">402</code> that carries the estimate so you can show
          the gap.
        </p>
        <CodeBlock
          code={`import json, os
from propalgos.client import Client, resolve_api_key

plan = json.load(open("plan.json"))

with Client(api_key=resolve_api_key(None, os.environ)) as client:
    job = client.launch_sweep(plan, idempotency_key="a-uuid-you-generated")
    final = client.wait_sweep(job["sweep_id"], timeout_s=5400, poll_s=15)

print(final["leaderboard"][:5])
print(final["usage_receipt"])`}
        />
      </div>

      {/* Job shape */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-white mb-3">Job fields</h3>
        <div className="overflow-x-auto">
          <table className="text-sm text-left w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="py-2 pr-4 text-gray-400 font-medium">Field</th>
                <th className="py-2 pr-4 text-gray-400 font-medium">Type</th>
                <th className="py-2 text-gray-400 font-medium">Description</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              {JOB_FIELDS.map(([field, type, desc]) => (
                <tr key={field} className="border-b border-gray-800/50 last:border-0">
                  <td className="py-1.5 pr-4 font-mono text-sky-400">{field}</td>
                  <td className="py-1.5 pr-4 text-gray-500">{type}</td>
                  <td className="py-1.5 text-gray-400">{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-gray-400 text-sm mt-3">
          <code className="text-gray-300">estimate_deviation</code> is routinely negative on{' '}
          <code className="text-gray-300">windows</code>: the estimate sizes walk-forward
          windows from the requested lookback, the run sizes them from the bars that actually
          exist. It is an accuracy signal, not an error. Cancelling an already-terminal sweep
          is a <code className="text-gray-300">409</code> — terminal statuses have no outgoing
          transitions.
        </p>
      </div>
    </section>
  );
}
